import clsx from 'clsx';
import GroupButtons from 'components/GroupButtons';
import { CALENDAR_VIEW_TYPE } from 'features/appointment/services/constants';
import { DATE_FORMAT } from 'features/appointment/utils/format';
import { IParams } from 'features/report/services/types/reportStaff';
import moment from 'moment';
import React, { useMemo } from 'react';
import { useAppDispatch } from 'store/hooks';
import styled from 'styled-components';
const DAILY = { label: 'Daily', value: CALENDAR_VIEW_TYPE.DAY_VIEW };
const WEEKLY = { label: 'Weekly', value: CALENDAR_VIEW_TYPE.WEEK_VIEW };
const TWO_WEEKS = { label: '2 Weeks', value: CALENDAR_VIEW_TYPE.TWO_WEEKS };
const MONTHLY = { label: 'Monthly', value: CALENDAR_VIEW_TYPE.MONTH_VIEW };
const YEARLY = { label: 'Yearly', value: CALENDAR_VIEW_TYPE.YEAR_VIEW };
const YEAR_COMPARISON = { label: 'Year Comparison', value: CALENDAR_VIEW_TYPE.YEAR_COMPARISON };

type ISwitchCalendarTypeProps = {
    actions: any,
    selectors: any,
    type: string,
    twoWeeks?: boolean;
    onChangeValue?: (data: {
        startTime: string,
        endTime: string,
    }) => void;
    v2?: boolean;
    yearly?: boolean;
    yearComparison? : boolean;
};
const SwitchCalendarType: React.FC<ISwitchCalendarTypeProps> = ({
    actions,
    selectors,
    twoWeeks = false,
    type = 'DEFAULT',
    onChangeValue = () => undefined,
    v2 = false,
    yearly = false,
    yearComparison = false,
}) => {
    const dispatch = useAppDispatch();
    const viewType = selectors.getViewType()?.[type] || CALENDAR_VIEW_TYPE.DAY_VIEW;
    const _options = useMemo(() => {
        if (yearComparison) return [DAILY, WEEKLY, MONTHLY, YEARLY, YEAR_COMPARISON];
        if (twoWeeks) return [DAILY, WEEKLY, TWO_WEEKS, MONTHLY];
        if (yearly) return [DAILY, WEEKLY, MONTHLY, YEARLY];
        return [DAILY, WEEKLY, MONTHLY];
    }, [twoWeeks, yearly, yearComparison]);

    const onChange = (val: string) => {
        const payload = {
            [type]: val as CALENDAR_VIEW_TYPE,
        };
        dispatch(actions.setCalendarViewType(payload));

        const now = moment();
        let params: IParams | null = null;
        switch (val) {
            case CALENDAR_VIEW_TYPE.DAY_VIEW: {
                params = {
                    [type]: {
                        startTime: now.format(DATE_FORMAT),
                        endTime: now.format(DATE_FORMAT)
                    }
                };
                onChangeValue({
                    startTime: now.format(DATE_FORMAT),
                    endTime: now.format(DATE_FORMAT)
                });
                break;
            }
            case CALENDAR_VIEW_TYPE.WEEK_VIEW: {
                const startTime = now.startOf('week').format(DATE_FORMAT);
                const endTime = now.endOf('week').format(DATE_FORMAT);
                params = {
                    [type]: {
                        startTime,
                        endTime
                    }
                };
                onChangeValue({
                    startTime: now.format(DATE_FORMAT),
                    endTime: now.format(DATE_FORMAT)
                });
                break;
            }
            case CALENDAR_VIEW_TYPE.TWO_WEEKS: {
                const endTime = now.format(DATE_FORMAT);
                const startTime = now.clone().subtract(15, 'day').format(DATE_FORMAT);
                params = {
                    [type]: {
                        startTime,
                        endTime
                    }
                };
                onChangeValue({
                    startTime: now.format(DATE_FORMAT),
                    endTime: now.format(DATE_FORMAT)
                });
                break;
            }
            case CALENDAR_VIEW_TYPE.MONTH_VIEW: {
                const startTime = now.startOf('month').format(DATE_FORMAT);
                const endTime = now.endOf('month').format(DATE_FORMAT);
                params = {
                    [type]: {
                        startTime,
                        endTime
                    }
                };
                onChangeValue({
                    startTime: now.format(DATE_FORMAT),
                    endTime: now.format(DATE_FORMAT)
                });
                break;
            }
            case CALENDAR_VIEW_TYPE.YEAR_VIEW: 
            case CALENDAR_VIEW_TYPE.YEAR_COMPARISON: {
                const startTime = now.startOf('year').format(DATE_FORMAT);
                const endTime = now.endOf('year').format(DATE_FORMAT);
                params = {
                    [type]: {
                        startTime,
                        endTime
                    }
                };
                onChangeValue({
                    startTime: now.format(DATE_FORMAT),
                    endTime: now.format(DATE_FORMAT)
                });
                break;
            }
            default:
                break;
        }

        if (params)
            dispatch(actions.setParams(params));
    };

    return (
        <SwitchCalendarTypeStyled className={clsx('SwitchCalendarTypeStyled', v2 && 'v2')}>
            <GroupButtons
                options={_options}
                value={viewType}
                onChange={onChange}
                className={_options?.includes(YEAR_COMPARISON) ? 'year-comparison' : ''}
            />
        </SwitchCalendarTypeStyled>
    );
};

export default SwitchCalendarType;
type SwitchCalendarTypeStyledProps = {}
const SwitchCalendarTypeStyled = styled.div<SwitchCalendarTypeStyledProps>`
&.v2 {
    .GroupButtons {
        border-radius: 4px;
        background: #E5E6EB;
        border: 1px solid #E5E6EB;
        button {
            width: 6rem;
            .BODY_1 {
                color: #1D2129;
                text-align: center;
                font-family: Poppins;
                font-size: 18px;
                font-style: normal;
                font-weight: 600;
                line-height: 30px;
                text-transform: capitalize;
            }

            &.active {
                border-radius: 4px;
                background: #232F3E;
                .BODY_1 {
                    color: #fff;
                }
            }
        }
    }
}
`;
